import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import type { CutscenePlayer, CutsceneSequence } from '@/gameplay/Cutscene';
import type { EnemyConfig } from '@/gameplay/Enemy';
import {
  applyMissionRewards,
  missionCompletedFlag,
  type MissionDefinition,
  type MissionProgressState,
  type MissionStatus,
} from '@/gameplay/Mission';
import { SURVIVORS_MISSION_ID } from '@/gameplay/missions/survivors';

export const DE_BEGRAFENIS_MISSION_ID = 'na-02-de-begrafenis';

/** De bestaande `workers`-safehouse-trigger telt als voltooiing van deze missie. */
export const DE_BEGRAFENIS_SAFEHOUSE_ID = 'workers';

export const DE_BEGRAFENIS_MISSION: MissionDefinition = {
  id: DE_BEGRAFENIS_MISSION_ID,
  title: 'De Begrafenis',
  act: 1,
  prerequisites: [
    { flag: missionCompletedFlag(SURVIVORS_MISSION_ID) },
  ],
  beats: [
    {
      id: 'setup',
      kind: 'setup',
      title: 'De begrafenis van Aldo',
      objective: 'Woon de begrafenis van Aldo Vitale bij',
      setup: 'De familie draagt Aldo naar zijn graf in de Arbeidersbuurt terwijl de stad toekijkt.',
    },
    {
      id: 'complication',
      kind: 'complication',
      title: 'De hinderlaag',
      objective: 'Overleef de hinderlaag en bereik de safehouse',
      trigger: 'Barili-schutters openen het vuur zodra de ceremonie eindigt.',
    },
    {
      id: 'reward',
      kind: 'reward',
      title: 'Veilig binnen',
      objective: 'Bereik de safehouse in de Arbeidersbuurt',
      onComplete: 'Luca overleeft de tweede aanslag.',
    },
  ],
  rewards: {
    flags: {
      [missionCompletedFlag(DE_BEGRAFENIS_MISSION_ID)]: true,
    },
    reputation: {
      populace: 2,
    },
  },
};

/**
 * Authored hinderlaag-posities op de hoofdstraat van de `workers`-wijk, tussen de ingang
 * (x=172) en de safehouse (x=240, z=-5.4). Geen nieuwe geometrie nodig (Assumptions,
 * spec.md): de bestaande straatstrook tussen de twee gebouwrijen (z ≈ -13.2 / z ≈ +13.2)
 * is breed genoeg om als hinderlaag te lezen.
 */
export const DE_BEGRAFENIS_AMBUSH: readonly EnemyConfig[] = [
  { id: 101, patrol: [new THREE.Vector2(222, -3), new THREE.Vector2(224, -1)] },
  { id: 102, patrol: [new THREE.Vector2(228, 4), new THREE.Vector2(230, 2)] },
  { id: 103, patrol: [new THREE.Vector2(234, -4), new THREE.Vector2(236, -2)] },
  { id: 104, patrol: [new THREE.Vector2(238, 3), new THREE.Vector2(236, 5)] },
];

const CEREMONY_FOCUS = new THREE.Vector3(230, 0, -2);

export function createDeBegrafenisSequence(
  definition: MissionDefinition = DE_BEGRAFENIS_MISSION,
): CutsceneSequence {
  return {
    id: definition.id,
    beats: [
      {
        duration: 3.5,
        camera: { focus: CEREMONY_FOCUS, viewHeight: 9, ease: 'smooth' },
        text: { line: 'De familie begraaft Aldo. De stad kijkt toe.' },
      },
      {
        duration: 2.5,
        camera: { focus: CEREMONY_FOCUS, viewHeight: 6.5, ease: 'smooth' },
        text: { speaker: 'Barili', line: 'Zelfs de doden krijgen bij ons geen rust.' },
      },
    ],
  };
}

export interface DeBegrafenisMissionConfig {
  readonly definition?: MissionDefinition;
  readonly state: MissionProgressState;
  readonly cutscene: CutscenePlayer;
  /** Wordt aangeroepen zodra de ceremonie eindigt (afgespeeld of overgeslagen); de aanroeper
   *  spawnt hier de `DE_BEGRAFENIS_AMBUSH`-configs in de bestaande enemy/combat/bodies-systemen. */
  readonly onAmbushStart?: () => void;
  readonly onComplete?: () => void;
}

/**
 * Missie 2 (NA-21): Luca woont Aldo's begrafenis bij in de Arbeidersbuurt. Zodra de korte
 * ceremonie-cutscene eindigt (afgespeeld of overgeslagen), opent Barili het vuur. De missie
 * voltooit zodra Luca de bestaande `workers`-safehouse bereikt — ongeacht hoeveel aanvallers
 * nog leven (FR-004) — en faalt alleen als Luca zelf sterft (`player:died`, FR-005); het
 * verlies van een niet-Luca NPC faalt de missie niet (FR-007, er zijn hier geen begeleiders).
 */
export class DeBegrafenisMission {
  readonly definition: MissionDefinition;
  readonly #state: MissionProgressState;
  readonly #cutscene: CutscenePlayer;
  readonly #onAmbushStart: (() => void) | undefined;
  readonly #onComplete: (() => void) | undefined;
  readonly #unsubscribers: Array<() => void> = [];

  #status: MissionStatus = 'running';
  #ambushStarted = false;

  constructor(config: DeBegrafenisMissionConfig) {
    this.definition = config.definition ?? DE_BEGRAFENIS_MISSION;
    this.#state = config.state;
    this.#cutscene = config.cutscene;
    this.#onAmbushStart = config.onAmbushStart;
    this.#onComplete = config.onComplete;
  }

  get id(): string {
    return this.definition.id;
  }

  get status(): MissionStatus {
    return this.#status;
  }

  start(): void {
    this.dispose();
    this.#status = 'running';
    this.#ambushStarted = false;
    this.#unsubscribers.push(
      bus.on('cutscene:ended', ({ id }) => {
        if (id === this.id) this.#startAmbush();
      }),
      bus.on('cutscene:skipped', ({ id }) => {
        if (id === this.id) this.#startAmbush();
      }),
      bus.on('safehouse:entered', ({ id }) => {
        if (id === DE_BEGRAFENIS_SAFEHOUSE_ID) this.#complete();
      }),
      bus.on('player:died', () => this.#fail('Luca is down')),
    );
    bus.emit('mission:objectiveChanged', { text: 'Woon de begrafenis van Aldo bij' });
    this.#cutscene.play(createDeBegrafenisSequence(this.definition));
  }

  dispose(): void {
    for (const unsubscribe of this.#unsubscribers) unsubscribe();
    this.#unsubscribers.length = 0;
  }

  #startAmbush(): void {
    if (this.#status !== 'running' || this.#ambushStarted) return;
    this.#ambushStarted = true;
    bus.emit('mission:objectiveChanged', { text: 'Overleef de hinderlaag en bereik de safehouse' });
    this.#onAmbushStart?.();
  }

  #complete(): void {
    if (this.#status !== 'running') return;
    this.#status = 'completed';
    applyMissionRewards(this.definition, this.#state);
    this.#state.setFlag(missionCompletedFlag(this.id), true);
    bus.emit('mission:completed', { id: this.id });
    this.dispose();
    this.#onComplete?.();
  }

  #fail(reason: string): void {
    if (this.#status !== 'running') return;
    this.#status = 'failed';
    bus.emit('mission:failed', { id: this.id, reason });
    this.dispose();
  }
}
